// Documentation export helpers - Markdown download + print-to-PDF, matching the printReport.js pattern.
const esc = (v) => String(v ?? '').replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]));
const absImg = (src) => (src && src.startsWith('/') ? `${window.location.origin}${src}` : src);
// Convert **bold** to <b> after escaping.
const mdBoldToHtml = (s) => esc(s).split('**').map((part, i) => (i % 2 ? `<b>${part}</b>` : part)).join('');

// ---------- Markdown ----------
function blockToMd(b) {
  if (b.h) return `### ${b.h}\n`;
  if (b.p) return `${b.p}\n`;
  if (b.list) return b.list.map((x) => `- ${x}`).join('\n') + '\n';
  if (b.steps) return b.steps.map((x, i) => `${i + 1}. ${x}`).join('\n') + '\n';
  if (b.code) return '```\n' + b.code + '\n```\n';
  if (b.callout) return `> ${b.tone === 'warn' ? '\u26a0\ufe0f ' : ''}${b.callout}\n`;
  if (b.img) return `![${b.cap || 'screenshot'}](${absImg(b.img)})\n${b.cap ? `*${b.cap}*\n` : ''}`;
  if (b.kv) return b.kv.map(([k, v]) => `- **${k}**: ${v}`).join('\n') + '\n';
  if (b.table) {
    const head = `| ${b.table.cols.join(' | ')} |`;
    const sep = `| ${b.table.cols.map(() => '---').join(' | ')} |`;
    const rows = b.table.rows.map((r) => `| ${r.join(' | ')} |`).join('\n');
    return `${head}\n${sep}\n${rows}\n`;
  }
  return '';
}

export function articleToMarkdown(a) {
  const roleLine = (a.roles || []).length ? `_For: ${a.roles.join(', ')}_\n\n` : '';
  const body = a.blocks.map(blockToMd).filter(Boolean).join('\n');
  return `# ${a.title}\n\n${a.summary ? `${a.summary}\n\n` : ''}${roleLine}${body}`;
}

export function allToMarkdown(articles, categories) {
  const byCat = categories.map((c) => {
    const arts = articles.filter((a) => a.cat === c.key);
    if (!arts.length) return '';
    return `\n\n---\n\n# ${c.label}\n\n${arts.map(articleToMarkdown).join('\n\n---\n\n')}`;
  }).join('');
  return `# ONRID Documentation\n\n_Complete platform documentation - generated ${new Date().toISOString().slice(0, 10)}_\n${byCat}`;
}

export function downloadText(filename, text, mime = 'text/markdown') {
  const url = URL.createObjectURL(new Blob([text], { type: `${mime};charset=utf-8` }));
  const a = document.createElement('a');
  a.href = url; a.download = filename; document.body.appendChild(a); a.click(); a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 4000);
}

// ---------- Print to PDF ----------
function blockToHtml(b) {
  if (b.h) return `<h3>${esc(b.h)}</h3>`;
  if (b.p) return `<p>${mdBoldToHtml(b.p)}</p>`;
  if (b.list) return `<ul>${b.list.map((x) => `<li>${mdBoldToHtml(x)}</li>`).join('')}</ul>`;
  if (b.steps) return `<ol>${b.steps.map((x) => `<li>${mdBoldToHtml(x)}</li>`).join('')}</ol>`;
  if (b.code) return `<pre>${esc(b.code)}</pre>`;
  if (b.callout) return `<div class="callout ${b.tone === 'warn' ? 'warn' : 'info'}">${mdBoldToHtml(b.callout)}</div>`;
  if (b.img) return `<figure><img src="${esc(absImg(b.img))}" alt="${esc(b.cap || '')}" />${b.cap ? `<figcaption>${esc(b.cap)}</figcaption>` : ''}</figure>`;
  if (b.kv) return `<table class="kv">${b.kv.map(([k, v]) => `<tr><th>${esc(k)}</th><td>${mdBoldToHtml(v)}</td></tr>`).join('')}</table>`;
  if (b.table) return `<table class="grid"><thead><tr>${b.table.cols.map((c) => `<th>${esc(c)}</th>`).join('')}</tr></thead><tbody>${b.table.rows.map((r) => `<tr>${r.map((c) => `<td>${mdBoldToHtml(c)}</td>`).join('')}</tr>`).join('')}</tbody></table>`;
  return '';
}

function articleHtml(a) {
  const roles = (a.roles || []).length ? `<p class="roles">For: ${a.roles.map(esc).join(', ')}</p>` : '';
  return `<section class="article"><h2>${esc(a.title)}</h2>${a.summary ? `<p class="summary">${esc(a.summary)}</p>` : ''}${roles}${a.blocks.map(blockToHtml).join('')}</section>`;
}

const PRINT_CSS = `
  @page { margin: 18mm; }
  * { box-sizing: border-box; }
  body { font-family: 'Segoe UI', system-ui, Arial, sans-serif; color: #0F172A; max-width: 780px; margin: 0 auto; line-height: 1.55; }
  .cover { text-align: center; border-bottom: 3px double #EA580C; padding-bottom: 20px; margin-bottom: 28px; }
  .cover h1 { font-size: 30px; margin: 0; letter-spacing: 0.5px; }
  .cover p { color: #64748B; font-size: 13px; margin: 6px 0 0; }
  .article { page-break-inside: avoid; margin-bottom: 30px; }
  h2 { font-size: 21px; color: #0F172A; border-bottom: 2px solid #F1F5F9; padding-bottom: 6px; margin: 30px 0 6px; }
  h3 { font-size: 15px; margin: 18px 0 6px; }
  p.summary { color: #475569; font-size: 14px; margin: 2px 0 8px; }
  p.roles { font-size: 10px; text-transform: uppercase; letter-spacing: 0.08em; color: #94A3B8; margin: 0 0 10px; }
  p, li { font-size: 13px; }
  ul, ol { margin: 6px 0 10px; padding-left: 22px; }
  li { margin-bottom: 4px; }
  pre { background: #0F172A; color: #E2E8F0; border-radius: 6px; padding: 10px 12px; font-size: 11px; overflow-x: auto; white-space: pre-wrap; }
  .callout { border-radius: 6px; padding: 8px 12px; margin: 12px 0; font-size: 13px; }
  .callout.info { border: 1px solid #BAE6FD; background: #F0F9FF; color: #075985; }
  .callout.warn { border: 1px solid #FCD34D; background: #FFFBEB; color: #92400E; }
  figure { margin: 14px 0; }
  figure img { width: 100%; max-width: 640px; border: 1px solid #E2E8F0; border-radius: 8px; }
  figcaption { font-size: 11px; color: #94A3B8; margin-top: 4px; }
  table { width: 100%; border-collapse: collapse; margin: 12px 0; font-size: 12px; }
  table.grid th { background: #F1F5F9; text-transform: uppercase; letter-spacing: 0.05em; font-size: 10px; color: #64748B; }
  table.grid th, table.grid td { border: 1px solid #E2E8F0; padding: 6px 10px; text-align: left; }
  table.kv th { text-align: left; width: 190px; padding: 6px 10px; vertical-align: top; font-size: 12px; }
  table.kv td { padding: 6px 10px; font-size: 12px; }
  table.kv tr { border-top: 1px solid #F1F5F9; }
`;

function openPrint(title, innerHtml) {
  const html = `<!DOCTYPE html><html><head><meta charset="utf-8"><title>${esc(title)}</title><style>${PRINT_CSS}</style></head>
<body onload="setTimeout(function(){window.print()},400)">
  <div class="cover"><h1>${esc(title)}</h1><p>ONRID - One Roll Identity &middot; Education OS documentation</p></div>
  ${innerHtml}
</body></html>`;
  const url = URL.createObjectURL(new Blob([html], { type: 'text/html' }));
  const w = window.open(url, '_blank', 'width=900,height=1000');
  if (!w) { URL.revokeObjectURL(url); return; }
  setTimeout(() => URL.revokeObjectURL(url), 60000);
}

export function printArticlePdf(a) {
  openPrint(a.title, articleHtml(a));
}

export function printAllPdf(articles, categories) {
  const inner = categories.map((c) => {
    const arts = articles.filter((x) => x.cat === c.key);
    if (!arts.length) return '';
    return `<h1 style="font-size:24px;color:#EA580C;margin-top:36px">${esc(c.label)}</h1>${arts.map(articleHtml).join('')}`;
  }).join('');
  openPrint('ONRID Documentation', inner);
}
